import chroma from "chroma-js";

/**
 * Creatas a palette of colours.
 * @param  {Number} totalColours The total number of colours to generate
 * @return {Array}       An array of colours in hex format, including the leading # character
 */
export const createColourPalette = totalColours => {
  return chroma
    .scale(["381460", "ffbd69"])
    .mode("lch")
    .colors(totalColours);
};

const translateToChromaColour = apiColour => {
  const colourMap = new Map([
    ["LIGHT_BLUE", "cornflowerblue"],
    ["BLACK", "black"],
    ["GREEN", "forestgreen"]
  ]);
  return chroma(colourMap.get(apiColour)).hex();
};

const newModelTemplate = (model) => {
  return {
    label: model.name,
    fill: false,
    borderColor: translateToChromaColour(model.colour),
    backgroundColor: translateToChromaColour(model.colour),
    data: [],
    pointStyle: "line"
  }
};

const newUpperConfidenceTemplate = () => {
  return {
  label: "Upper confidence interval",
  fill: false,
  borderColor: chroma("green").alpha(0.2).hex(),
  data: [],
  pointStyle: "line"
}};

const newLowerConfidenceTemplate = () => {
  return {
  label: "Lower confidence interval",
  fill: 1,
  borderColor: chroma("green").alpha(0.2).hex(),
  backgroundColor: chroma("green").alpha(0.2).hex(),
  data: [],
  pointStyle: "line"
}};

/**
 * Generates a dataset object for chart.js populated with model score data
 * @param  {Array} modeldata Array containing the model metadata and scores
 * @return {Object}          The datasets to be passed to chart.js
 * @see {@link https://www.chartjs.org/docs/latest/charts/line.html#data-structure}
 */
export const generateChartData = modeldata => {
  const isAnArray = Array.isArray(modeldata);

  if (isAnArray && modeldata.length === 0) {
    return {};
  }

  if (isAnArray) {
    const template = {
      datasets: []
    };
    modeldata.forEach((model, index) => {
      const modelTemplate = newModelTemplate(model);
      if (model.hasConfidenceInterval) {
        const upperConfidenceTemplate = newUpperConfidenceTemplate();
        const lowerConfidenceTemplate = newLowerConfidenceTemplate();
        model.datapoints.slice().forEach((datapoint) => {
          const date = new Date(Date.parse(datapoint.score_date));
          modelTemplate.data.push({ x: date, y: datapoint.score_value });
          upperConfidenceTemplate.data.push(
            { x: date, y: datapoint.confidence_interval_upper }
          );
          lowerConfidenceTemplate.data.push(
            { x: date, y: datapoint.confidence_interval_lower }
          );
        });
        lowerConfidenceTemplate.fill = index;
        template.datasets.push(upperConfidenceTemplate);
        template.datasets.push(lowerConfidenceTemplate);
      } else {
        model.datapoints.slice().forEach((datapoint) => {
          const date = new Date(Date.parse(datapoint.score_date));
          modelTemplate.data.push({ x: date, y: datapoint.score_value });
        });

      }
      template.datasets.push(modelTemplate);
    });
    return template;
  }

  return {};
};

/**
 * Configuration options for Chart.js, allowing the setting of annotations
 * which is used for drawing threshold lines
 * @see {@link https://www.chartjs.org/docs/latest/developers/updates.html#updating-options}
 */
export const chartOptions = {
  title: {
    display: false
  },
  scales: {
    x: {
      type: "time",
      time: {
        displayFormats: {
          week: "d MMM yyyy",
        },
        // Format tokens https://date-fns.org/docs/format
        tooltipFormat: "do 'of' MMM, yyyy",
        unit: "week",
        stepSize: 1
      },
      ticks: {
        fontSize: 14
      }
    },
    y: {
      ticks: {
        beginAtZero: true,
        fontSize: 14
      },
      scaleLabel: {
        display: true,
        labelString: "Estimated COVID-19 prevalence",
        fontSize: 16,
        fontStyle: 'bold'
      }
    },

  },
  tooltips: {
    backgroundColor: "rgba(255,255,255,0.8)",
    bodyFontColor: "#666",
    bodyFontStyle: "bold",
    titleFontColor: "#666"
  },
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: {
        fontSize: 14,
        usePointStyle: false,
        boxWidth: 15,
        filter: (item, chart) => {
          return !item.text.includes("confidence interval");
        }
      }
    },
    annotation: {
      drawTime: 'afterDatasetsDraw',
      annotations: [
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2020-03-11')),
          borderColor: '#0066cc',
          borderWidth: 2,
          borderDash: [2, 2],
          label: {
            backgroundColor: 'rgba(0,0,0,0.05)',
            color: '#666',
            position: 'start',
            xAdjust: -22,
            rotation: 270,
            enabled: true,
            content: 'physical distancing measures'
          }
        },
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2020-03-24')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 1,
          label: {
            backgroundColor: 'rgba(0,0,0,0.05)',
            color: '#666',
            position: 'start',
            xAdjust: 22,
            rotation: 270,
            enabled: true,
            content: 'lockdown measures'
          }
        },
        {
          type: 'box',
          xScaleID: 'x',
          xMin: new Date(Date.parse('2020-03-24')),
          xMax: new Date(Date.parse('2020-06-01')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 0.01,
          backgroundColor: 'rgba(229, 115, 115, 0.1)'
        },
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2020-06-01')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 1
        },
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2020-11-05')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 1,
          label: {
            backgroundColor: 'rgba(0,0,0,0.05)',
            color: '#666',
            position: 'start',
            xAdjust: -22,
            rotation: 270,
            enabled: true,
            content: 'lockdown measures'
          }
        },
        {
          type: 'box',
          xScaleID: 'x',
          xMin: new Date(Date.parse('2020-11-05')),
          xMax: new Date(Date.parse('2020-12-02')),
          borderColor: '#8B008B',
          borderWidth: 0.01,
          backgroundColor: 'rgba(229, 115, 115, 0.1)'
        },
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2020-12-02')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 1
        },
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2021-01-06')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 1,
          label: {
            backgroundColor: 'rgba(0,0,0,0.05)',
            color: '#666',
            position: 'start',
            xAdjust: 22,
            rotation: 270,
            enabled: true,
            content: 'lockdown measures'
          }
        },
        {
          type: 'box',
          xScaleID: 'x',
          xMin: new Date(Date.parse('2021-01-06')),
          xMax: new Date(Date.parse('2021-03-29')),
          borderColor: '#8B008B',
          borderWidth: 0.01,
          backgroundColor: 'rgba(229, 115, 115, 0.1)'
        },
        {
          type: 'line',
          mode: 'vertical',
          scaleID: 'x',
          value: new Date(Date.parse('2021-03-29')),
          borderColor: 'rgb(229, 115, 115)',
          borderWidth: 1
        }
      ]
    }
  }
};
